import { makeAutoObservable, runInAction } from "mobx";
import React, { useContext } from "react";
import { OsaNodeCoding } from "../../coding/models/OsaNodeCoding";
import { FeatureCollection } from "geojson";
import {
  parseAttributeInputValue,
  setAttributeByName,
} from "../../coding/utils/attribute-value";
import { ForecastScenarioApi } from "../services/ForecastScenarioApi";
import {
  AttributeDefinitions,
  AttributeName,
  DistanceUnit,
  IScore,
  Score,
} from "@keita/lanepatrol-sdk";
import {
  CreateForecastScenarioRequest,
  ForecastAction,
  ForecastActionType,
  ForecastChange,
  ForecastScenario,
  SimulatedResult,
  UpdateForecastScenarioRequest,
} from "../models/ForecastScenario";
import { RootStore } from "../../shared/stores/RootStore";
import { OsaNode } from "../../osaway/models/OsaNode";
import { ProjectApi } from "../services/ProjectApi";
import {
  flattenBidimensionalMap,
  unflattenBidimensionalMap,
} from "../../shared/utils/object-utils";
import {
  scoreFromValues,
  scoreToGeoJsonNodeScore,
} from "../../coding/utils/score-utils";
import { OsaNodeCodingApi } from "../../coding/services/OsaNodeCodingApi";
import { applyTreatmentToOsaNodeCoding } from "../utils/treatment-changes";
import { ResultsFilters } from "../../map/models/MapFilters";
import {
  isNodeVisibleByAttributes,
  isNodeVisibleByScore,
  isNodeVisibleByTreatment,
  isOsaWayVisibleThroughFilters,
} from "../utils/apply-filters";
import { LifeCycleStage } from "../../osaway/models/LifeCycleStage";
import { OsaWay } from "../../osaway/models/OsaWay";

type osaWayId = string;
type osaNodeId = string;

export class ForecastStore {
  rootStore;
  forecastChanges: ForecastChange[] = [];
  computedValues?: {
    allNodeCodingsByWayId: Map<osaWayId, Map<osaNodeId, OsaNodeCoding>>;
    osaNodes: Map<osaWayId, OsaNode[]>;
    osaWays: OsaWay[];
  };
  forecastScenarios: ForecastScenario[] = [];
  selectedScenario?: ForecastScenario;
  computingValues = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  resetState() {
    this.restoreOriginal();
    runInAction(() => {
      this.forecastScenarios = [];
      this.computingValues = false;
    });
  }

  restoreOriginal() {
    runInAction(() => {
      this.forecastChanges = [];
      this.computedValues = undefined;
      this.selectedScenario = undefined;
    });
  }

  isForecastModeActivated() {
    return !!this.forecastChanges.length;
  }

  addChange(forecastAction: ForecastAction) {
    const { filterStore } = this.rootStore;
    runInAction(() => {
      this.forecastChanges = this.forecastChanges.concat([
        {
          action: forecastAction,
          filters: {
            osaWayFilters: filterStore.osaWayFilters,
            scoreFilters: filterStore.scoreFilters,
            treatmentFilters: filterStore.treatmentFilters,
            attributeFilters: filterStore.attributeFilters,
          },
        },
      ]);
    });
    this.computeValues();
  }

  removeChangeByIndex(index: number) {
    runInAction(() => {
      this.forecastChanges = this.forecastChanges
        .filter((_, i) => i !== index)
        .map((change, i) =>
          i < index ? change : { ...change, results: undefined },
        );
    });
    this.computeValues();
  }

  setSelectedScenario(scenarioId: string | undefined) {
    const forecastScenario = this.forecastScenarios.find(
      (scenario) => scenario.id === scenarioId,
    );

    if (forecastScenario) {
      runInAction(() => {
        this.selectedScenario = forecastScenario;
        this.forecastChanges = forecastScenario.changes;
      });
    } else {
      this.restoreOriginal();
    }

    this.computeValues();
  }

  async createForecastScenario(request: CreateForecastScenarioRequest) {
    const response = await ForecastScenarioApi.create(request);
    runInAction(() => {
      this.forecastScenarios = [...this.forecastScenarios, response.data];
      this.selectedScenario = response.data;
    });
    return response;
  }

  async updateForecastScenario(
    id: string,
    request: UpdateForecastScenarioRequest,
  ) {
    const response = await ForecastScenarioApi.update(id, request);
    runInAction(() => {
      this.forecastScenarios = this.forecastScenarios.map((item) =>
        item.id === id ? response.data : item,
      );
      this.selectedScenario = response.data;
    });
    return response;
  }

  async deleteForecastScenario(id: string) {
    await ForecastScenarioApi.delete(id);
    runInAction(() => {
      this.forecastScenarios = this.forecastScenarios.filter(
        (item) => item.id !== id,
      );
      this.selectedScenario = undefined;
    });
  }

  async fetchForecastScenarios(projectId: string, cancelIf?: () => boolean) {
    const response = await ProjectApi.listForecastScenarios(projectId);
    if (cancelIf?.()) return;

    runInAction(() => {
      this.forecastScenarios = response.data;
    });
  }

  private forceCodedLifeCycleStageFilters() {
    const { filterStore } = this.rootStore;
    filterStore.forceCodedLifeCycleStageFilters(
      filterStore.lifeCycleStageFilters,
    );
  }

  private async computeValues() {
    const { projectStore } = this.rootStore;

    if (!this.isForecastModeActivated()) {
      runInAction(() => {
        this.computedValues = undefined;
      });
      return;
    }

    this.forceCodedLifeCycleStageFilters();

    runInAction(() => {
      this.computingValues = true;
    });

    try {
      const { filterStore, osaNodeStore, codingStore } = this.rootStore;
      const originalOsaWays = filterStore.osaWaysAvailableToUser;
      const originalCodingEntries = codingStore.allNodeCodingsByWayId;
      const originalOsaNodes = osaNodeStore.nodesByWayId;

      const { forecastedOsaWays, forecastedNodes, forecastedCodingEntries } =
        flattenOriginalData(
          originalOsaWays,
          originalOsaNodes,
          originalCodingEntries,
        );

      for (const change of this.forecastChanges) {
        const affectedKeyPairs = filterAffectedNodes(
          forecastedNodes,
          forecastedCodingEntries,
          change.filters,
        );

        changeForecastedCodingEntries(
          change,
          affectedKeyPairs,
          forecastedCodingEntries,
          forecastedNodes,
        );

        if (!change.results) {
          const affectedCodingEntries = new Map(
            affectedKeyPairs.map(([osaWayId, osaNodeId]) => [
              `${osaWayId},${osaNodeId}`,
              forecastedCodingEntries.get(`${osaWayId},${osaNodeId}`)!,
            ]),
          );

          const response = await simulateChangeResults(
            affectedCodingEntries,
            projectStore.distanceUnit,
          );
          runInAction(() => {
            change.results = response;
          });
        }

        applyComputedValuesToForecastedData(
          change.results!,
          forecastedOsaWays,
          forecastedNodes,
        );
      }

      runInAction(() => {
        this.computingValues = false;
        this.computedValues = unflattenForecastedData(
          forecastedOsaWays,
          forecastedNodes,
          forecastedCodingEntries,
        );
      });
    } catch (error) {
      console.error(error);
      runInAction(() => {
        this.computingValues = false;
      });
    }
  }
}

interface ContextProps {
  forecastStore: ForecastStore;
}
export const ForecastStoreContext = React.createContext({} as ContextProps);

export const useForecastStoreContext = () => useContext(ForecastStoreContext);

function flattenOriginalData(
  originalOsaWays: OsaWay[],
  originalNodes: Map<osaWayId, OsaNode[]>,
  originalCodingEntries: Map<osaWayId, Map<osaNodeId, OsaNodeCoding>>,
) {
  const filterCodedWays = (way: OsaWay) =>
    way.lifeCycleStage === LifeCycleStage.Review ||
    way.lifeCycleStage === LifeCycleStage.Completed;

  const forecastedOsaWays = new Map<osaWayId, OsaWay>(
    originalOsaWays
      .filter(filterCodedWays)
      .map((way) => [way.id, JSON.parse(JSON.stringify(way))]),
  );

  const forecastedNodes = new Map<osaWayId, OsaNode>(
    [...originalNodes.entries()].flatMap(([osaWayId, nodes]) => {
      if (!forecastedOsaWays.has(osaWayId)) return [];
      return nodes.map((node) => [node.id, JSON.parse(JSON.stringify(node))]);
    }),
  );

  const forecastedCodingEntries = flattenBidimensionalMap(
    originalCodingEntries,
    (entry) => JSON.parse(JSON.stringify(entry)),
  );

  return { forecastedOsaWays, forecastedNodes, forecastedCodingEntries };
}

function unflattenForecastedData(
  forecastedOsaWays: Map<osaWayId, OsaWay>,
  forecastedNodes: Map<osaNodeId, OsaNode>,
  forecastedCodingEntries: Map<string, OsaNodeCoding>,
) {
  const osaWays: OsaWay[] = [...forecastedOsaWays.values()];

  const osaNodes = new Map<osaWayId, OsaNode[]>();
  for (const node of forecastedNodes.values()) {
    const nodeList = osaNodes.get(node.osaWayId);
    if (nodeList) {
      nodeList.push(node);
    } else {
      osaNodes.set(node.osaWayId, [node]);
    }
  }

  const allNodeCodingsByWayId = unflattenBidimensionalMap(
    forecastedCodingEntries,
  );

  return { osaWays, osaNodes, allNodeCodingsByWayId };
}

function applyComputedValuesToForecastedData(
  simulatedResults: Map<string, SimulatedResult>,
  forecastedOsaWays: Map<string, OsaWay>,
  forecastedNodes: Map<string, OsaNode>,
) {
  const scoresByRouteId = new Map<string, (Score | null)[]>();

  for (const [keys, simulatedResult] of simulatedResults) {
    const [osaWayId, osaNodeId] = keys.split(",");

    const node = forecastedNodes.get(osaNodeId)!;
    forecastedNodes.set(osaNodeId, {
      ...node,
      score: simulatedResult.score,
      scoreImpact: simulatedResult.scoreImpact,
      treatments: simulatedResult.treatments,
    });

    // update road with new section score
    const way = forecastedOsaWays.get(osaWayId)!;
    if (way.geoJson) {
      const geoJson: FeatureCollection = {
        ...way.geoJson,
        features: way.geoJson.features.map((feature) => {
          if (feature.properties?.id === osaNodeId) {
            return {
              ...feature,
              properties: {
                ...feature.properties,
                color: simulatedResult.score.global.color,
                ...scoreToGeoJsonNodeScore(simulatedResult.score),
              },
            };
          } else {
            return feature;
          }
        }),
      };
      way.geoJson = geoJson;
    }

    scoresByRouteId.set(
      osaWayId,
      (scoresByRouteId.get(osaWayId) ?? []).concat([simulatedResult.score]),
    );
  }

  // re-calculate way scores, by averaging out node scores
  for (const [osaWayId, forecastedScores] of scoresByRouteId) {
    const way = forecastedOsaWays.get(osaWayId)!;
    const totalScores = forecastedScores.length;

    if (way?.score) {
      // if original score is null, there wont be a forecasted score either
      const routeScoreValues = forecastedScores.reduce(
        (scoreSum, score) => {
          scoreSum.global += score!.global.value / totalScores;
          scoreSum.bicycleToBicycle +=
            score!.bicycleToBicycle.value / totalScores;
          scoreSum.bicycleToPedestrian +=
            score!.bicycleToPedestrian.value / totalScores;
          scoreSum.vehicleToBicycle +=
            score!.vehicleToBicycle.value / totalScores;
          scoreSum.singleBicycle += score!.singleBicycle.value / totalScores;

          return scoreSum;
        },
        {
          global: 0,
          bicycleToBicycle: 0,
          bicycleToPedestrian: 0,
          vehicleToBicycle: 0,
          singleBicycle: 0,
        } as IScore<number>,
      );

      way.score = scoreFromValues(routeScoreValues);
    }
  }
}

async function simulateChangeResults(
  flatAffectedCodingEntries: Map<string, OsaNodeCoding>,
  projectDistanceUnit: DistanceUnit,
): Promise<Map<string, SimulatedResult>> {
  const affectedKeyPairs = [...flatAffectedCodingEntries.keys()];
  const affectedCodingEntries = [...flatAffectedCodingEntries.values()];

  const chunkSize = 500;
  var allResults: SimulatedResult[] = [];

  for (let i = 0; i < affectedCodingEntries.length; i += chunkSize) {
    const chunk = affectedCodingEntries.slice(i, i + chunkSize);

    const response = await OsaNodeCodingApi.simulateResults({
      distanceUnit: projectDistanceUnit,
      attributes: chunk.map((ref) => ref.attributes),
    });

    allResults = allResults.concat(response.data);
  }

  return new Map(
    allResults.map((simulatedResult, i) => [
      affectedKeyPairs[i],
      simulatedResult,
    ]),
  );
}

function changeForecastedCodingEntries(
  change: ForecastChange,
  affectedKeyPairs: [osaWayId, osaNodeId][],
  forecastedCodingEntries: Map<string, OsaNodeCoding>,
  forecastedNodes: Map<string, OsaNode>,
) {
  switch (change.action.type) {
    case ForecastActionType.AttributeValues: {
      for (const [osaWayId, osaNodeId] of affectedKeyPairs) {
        const codingEntry = forecastedCodingEntries.get(
          `${osaWayId},${osaNodeId}`,
        )!;

        for (const [attributeName, stringValue] of Object.entries(
          change.action.attributeValues,
        ) as [AttributeName, string][]) {
          const attributeDefinition =
            AttributeDefinitions.getByName(attributeName)!;
          const value = parseAttributeInputValue(
            attributeDefinition,
            stringValue,
          );
          codingEntry.attributes = setAttributeByName(
            codingEntry.attributes,
            attributeName,
            value,
          );
        }
      }
      break;
    }

    case ForecastActionType.HighestPriorityTreatment: {
      for (const [osaWayId, osaNodeId] of affectedKeyPairs) {
        const codingEntry = forecastedCodingEntries.get(
          `${osaWayId},${osaNodeId}`,
        )!;

        const node = forecastedNodes.get(osaNodeId)!;

        if (node.treatments?.length) {
          const [highestPriorityTreatment] = node.treatments;
          applyTreatmentToOsaNodeCoding(
            highestPriorityTreatment.type,
            codingEntry,
          );
        }
      }
      break;
    }

    case ForecastActionType.TreatmentWhenSuggested: {
      for (const [osaWayId, osaNodeId] of affectedKeyPairs) {
        const codingEntry = forecastedCodingEntries.get(
          `${osaWayId},${osaNodeId}`,
        )!;

        const node = forecastedNodes.get(osaNodeId)!;
        const treatmentType = change.action.treatmentType;

        if (node.treatments?.find((t) => t.type === treatmentType)) {
          applyTreatmentToOsaNodeCoding(treatmentType, codingEntry);
        }
      }
      break;
    }

    default: {
      console.warn("Change not implemented yet.");
    }
  }
}

function filterAffectedNodes(
  forecastedNodes: Map<osaNodeId, OsaNode>,
  forecastedCodingEntries: Map<string, OsaNodeCoding>,
  filters: ResultsFilters,
) {
  const affectedNodes: [osaWayId, osaNodeId][] = [];

  for (const node of forecastedNodes.values()) {
    const codingEntry = forecastedCodingEntries.get(
      `${node.osaWayId},${node.id}`,
    )!;
    if (
      isOsaWayVisibleThroughFilters(node.osaWayId, filters.osaWayFilters) &&
      isNodeVisibleByAttributes(
        codingEntry.attributes,
        filters.attributeFilters,
      ) &&
      isNodeVisibleByScore(node.score!, filters.scoreFilters) &&
      isNodeVisibleByTreatment(node.treatments, filters.treatmentFilters)
    ) {
      affectedNodes.push([node.osaWayId, node.id]);
    }
  }

  return affectedNodes;
}
